/**
 * Match-status helpers.
 *
 * Opta's `liveData.matchDetails.matchStatus` is a string. We expose a small set
 * of predicates rather than have callers compare strings directly.
 */

import { getMatchISODate } from '$utils/dates';

/** @typedef {import('./types').Match} Match */

/**
 * Read the raw match status string, guarding against missing data.
 *
 * @param {Match} [match]
 * @returns {string | undefined}
 */
const getMatchStatus = (match) => match?.liveData?.matchDetails?.matchStatus;

/**
 * True when the match has not started yet.
 * Returns `undefined` if status data is unavailable.
 *
 * @param {Match} [match]
 * @returns {boolean | undefined}
 */
export const isUpcoming = (match) => {
  const status = getMatchStatus(match);
  return status === undefined ? undefined : status === 'Fixture';
};

/**
 * True when the match is currently in progress.
 * Returns `undefined` if status data is unavailable.
 *
 * @param {Match} [match]
 * @returns {boolean | undefined}
 */
export const isPlaying = (match) => {
  const status = getMatchStatus(match);
  return status === undefined ? undefined : status === 'Playing';
};

/**
 * True when the match has finished.
 * Returns `undefined` if status data is unavailable.
 *
 * @param {Match} [match]
 * @returns {boolean | undefined}
 */
export const hasPlayed = (match) => {
  const status = getMatchStatus(match);
  return status === undefined ? undefined : status === 'Played';
};

/**
 * True when the match has just finished for Today.
 * Returns `undefined` if status data is unavailable.
 *
 * @param {Match} [match]
 * @returns {boolean | undefined}
 */
export const hasJustPlayed = (match) => {
  const status = getMatchStatus(match);
  const timeDifference =
    new Date() - new Date(getMatchISODate(match.matchInfo));
  const hours = 20;
  const HOURS_AGO_MS = hours * 60 * 60 * 1000; // hours in milliseconds

  // Checks if the difference is positive (in the past) and under ago hours
  const isHoursAgo = timeDifference > 0 && timeDifference < HOURS_AGO_MS;
  return status === undefined ? undefined : status === 'Played' && isHoursAgo;
};
